import {
  Button,
  Dialog,
  DialogFooter,
  Flag,
  Form,
  FormButton,
  FormControl,
  FormField,
  FormSubmitHandler,
  Separator,
} from 'platform/components';
import {Box, Hide, HStack, Stack, Text, VStack} from 'platform/foundation';

import {useFormState, useWatch} from 'react-hook-form';

import {RelativeFilterConfigBody, RelativeFilterResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';

import {TestIdProps, suffixTestId} from 'shared/web';

import {getPresetTags} from '../utils/getPresetTags';
import {
  PresetEditorFormValues,
  buildPresetEditorDefaultValues,
  getRelativeFilterConfigFromPresetEditorForm,
  presetEditorFormSchema,
} from '../utils/presetEditorFormSchema';
import {BoundariesSection} from './BoundariesSection';
import {PropertiesSection} from './PropertiesSection';

interface PresetEditorDialogProps extends TestIdProps {
  isOpen: boolean;
  /** null means create mode */
  initialPreset: RelativeFilterResponseBody | null;
  onSave: (name: string, config: RelativeFilterConfigBody) => void;
  onDelete?: () => void;
  onClose: () => void;
}

type PresetEditorDialogFormBodyProps = PresetEditorDialogProps & {
  control: FormControl<PresetEditorFormValues>;
  isReadOnly: boolean;
};

const PRESET_SUMMARY_CONFIG_WATCH_NAMES = [
  'year',
  'mileage',
  'mileagePct',
  'power',
  'powerPct',
  'price',
  'pricePct',
  'featureScore',
  'trimLevel',
  'transmission',
  'fuel',
  'body',
  'drive',
  'vatDeductible',
  'sellerType',
  'seats',
  'radius',
] as const;

function PresetEditorDialogFormBody(props: PresetEditorDialogFormBodyProps) {
  const {control, isReadOnly} = props;

  const [
    wYear,
    wMileage,
    wMileagePct,
    wPower,
    wPowerPct,
    wPrice,
    wPricePct,
    wFeatureScore,
    wTrimLevel,
    wTransmission,
    wFuel,
    wBody,
    wDrive,
    wVatDeductible,
    wSellerType,
    wSeats,
    wRadius,
  ] = useWatch({
    control,
    name: [...PRESET_SUMMARY_CONFIG_WATCH_NAMES],
  });

  const summaryConfig: RelativeFilterConfigBody = {
    year: wYear ?? null,
    mileage: wMileage ?? null,
    mileagePct: wMileagePct ?? null,
    power: wPower ?? null,
    powerPct: wPowerPct ?? null,
    price: wPrice ?? null,
    pricePct: wPricePct ?? null,
    featureScore: wFeatureScore ?? null,
    trimLevel: wTrimLevel ?? null,
    transmission: wTransmission ?? null,
    fuel: wFuel ?? null,
    body: wBody ?? null,
    drive: wDrive ?? null,
    vatDeductible: wVatDeductible ?? null,
    sellerType: wSellerType ?? null,
    seats: wSeats ?? null,
    radius: wRadius ?? null,
  };

  const {isValid} = useFormState<PresetEditorFormValues>();
  const summaryTags = getPresetTags(summaryConfig);

  return (
    <>
      <VStack spacing={4}>
        <Stack direction={['column', 'row']} align="flex-start" justify="space-between" spacing={4}>
          <Box width={68} flexShrink={0}>
            <FormField
              control={control}
              name="name"
              type="text"
              label={i18n.t('entity.sourcing.relativeFilters.dialog.presetName')}
              isRequired
              isDisabled={isReadOnly}
              data-testid={suffixTestId('nameInput', props)}
            />
          </Box>
          {summaryTags.length > 0 ? (
            <HStack spacing={1} justify={[undefined, 'flex-end']} wrap>
              {summaryTags.map((tag) => (
                <Flag key={tag.id} label={tag.label} size="small" colorScheme="blue" isSubtle />
              ))}
            </HStack>
          ) : (
            <Text size="xSmall" color="secondary">
              {i18n.t('entity.sourcing.relativeFilters.summary.noConstraints')}
            </Text>
          )}
        </Stack>

        <Separator spacing={0} />

        <Stack direction={['column', 'row']} spacing={[4, 0]}>
          <Box flexGrow={1}>
            <BoundariesSection isReadOnly={isReadOnly} data-testid={props['data-testid']} />
          </Box>
          <Separator orientation="vertical" spacing={4} />
          <Box flexGrow={1}>
            <PropertiesSection isReadOnly={isReadOnly} />
          </Box>
        </Stack>
      </VStack>

      <DialogFooter data-testid={suffixTestId('footer', props)}>
        <HStack justify="space-between">
          <Hide when={isReadOnly || !props.onDelete}>
            <Button
              title={i18n.t('entity.sourcing.relativeFilters.dialog.delete')}
              leftIcon="action/delete_outline"
              variant="dangerOutlined"
              type="button"
              onClick={props.onDelete}
              data-testid={suffixTestId('deleteButton', props)}
            />
          </Hide>
          <HStack spacing={2} justify="flex-end" grow={1}>
            {isReadOnly ? (
              <Button
                title={i18n.t('general.actions.close')}
                variant="secondary"
                type="button"
                onClick={props.onClose}
                data-testid={suffixTestId('closeButton', props)}
              />
            ) : (
              <>
                <Button
                  title={i18n.t('general.actions.cancel')}
                  variant="secondary"
                  type="button"
                  onClick={props.onClose}
                  data-testid={suffixTestId('cancelButton', props)}
                />
                <FormButton
                  control={control}
                  type="submit"
                  title={i18n.t('general.actions.save')}
                  isDisabled={!isValid}
                  data-testid={suffixTestId('saveButton', props)}
                />
              </>
            )}
          </HStack>
        </HStack>
      </DialogFooter>
    </>
  );
}

export function PresetEditorDialog(props: PresetEditorDialogProps) {
  const isSystem = props.initialPreset?.system ?? false;
  const isReadOnly = isSystem;

  const defaultValues = buildPresetEditorDefaultValues(props.initialPreset);

  const onSubmit: FormSubmitHandler<PresetEditorFormValues> = (values) => {
    props.onSave(values.name.trim(), getRelativeFilterConfigFromPresetEditorForm(values));
    return Promise.resolve();
  };

  return (
    <Dialog
      title={i18n.t('entity.sourcing.relativeFilters.dialog.title')}
      isOpen={props.isOpen}
      onClose={props.onClose}
      withAdditionalFooter
      scrollBehavior="inside"
      data-testid={suffixTestId('dialog', props)}
    >
      <Form<PresetEditorFormValues>
        experimentalZodSchema={presetEditorFormSchema}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        isDisabled={isReadOnly}
        mode="onChange"
      >
        {(control) => (
          <PresetEditorDialogFormBody control={control} isReadOnly={isReadOnly} {...props} />
        )}
      </Form>
    </Dialog>
  );
}
